import { useState } from 'react'
import { FolderOpen, ExternalLink, FolderSearch, Pencil } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useUpdateAccount } from '@/hooks/use-api'
import { useToast } from '@/hooks/use-toast'
import { explainError } from '@/lib/explain-error'
import { contractsRootUrl } from '@/lib/contracts'

/**
 * A way into this customer's contracts, which live in Google Drive.
 *
 *
 * THE CRM HOLDS A LINK, NOT A LISTING
 *
 * There is deliberately no file list here. Listing a Drive folder needs a server-side API
 * call, and the backend has no Drive credential: GRM's per-user OAuth tokens carry Calendar
 * and Gmail scopes only, as one bundled set, so adding Drive would force every connected
 * user to reconnect. The alternative, a service account, needs the folder shared with it
 * and the Drive API enabled.
 *
 * A link needs neither. The folder opens under the user's OWN Google session, so Drive's
 * sharing decides who can read what, exactly as it does today, and no file or folder names
 * are stored in the CRM at all. What it costs is that the CRM can say where the contracts
 * are, not what is in them. That was the trade taken: one click to the right folder.
 *
 *
 * THE FALLBACK OPENS THE LIBRARY, IT DOES NOT SEARCH
 *
 * An earlier version searched Drive for the company name, which searched the viewer's
 * WHOLE Drive: a company name appearing in an unrelated document came back beside the
 * contract. A result list that mixes the two is worse than none, because somebody opens the
 * wrong file believing the CRM sent them there.
 *
 * So with no exact link recorded this opens the contracts library at its root and the
 * person picks the letter and the company. Two clicks rather than one, always correct. See
 * `lib/contracts.ts` for why there is no folder-scoped search here yet.
 */

interface ContractsTabProps {
  accountId: string
  accountName?: string
  /** The exact folder link, when somebody has recorded one. */
  contractsFolderUrl?: string | null
  /** Editing the link is an admin action, like other account-level configuration. */
  canEdit?: boolean
}

export function ContractsTab({
  accountId,
  accountName,
  contractsFolderUrl,
  canEdit = false,
}: ContractsTabProps) {
  const { toast } = useToast()
  const updateAccount = useUpdateAccount()

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(contractsFolderUrl ?? '')
  const [problem, setProblem] = useState<string | null>(null)

  const hasExactLink = Boolean(contractsFolderUrl)

  const save = async (value: string) => {
    const trimmed = value.trim()

    // Checked here as well as on the server so a typo is answered immediately rather than
    // after a round trip. The server and the column both enforce it too: this value becomes
    // a link people click from inside the CRM, so it must not be able to point anywhere.
    if (trimmed && !/^https:\/\/(drive|docs)\.google\.com\//.test(trimmed)) {
      setProblem(
        'That is not a Google Drive link. Open the folder in Drive and copy the address ' +
          'from the browser, which starts with https://drive.google.com/.'
      )
      return
    }

    setProblem(null)
    try {
      await updateAccount.mutateAsync({
        id: accountId,
        data: { contracts_folder_url: trimmed || null },
      })
      setEditing(false)
      toast({
        title: trimmed ? 'Contracts folder saved' : 'Contracts folder cleared',
        description: trimmed
          ? 'This account now links straight to its folder in Drive.'
          : 'This account falls back to a Drive search again.',
      })
    } catch (error) {
      setProblem(explainError(error, 'The contracts folder link could not be saved'))
    }
  }

  return (
    <Card>
      <CardContent className="py-5 space-y-4">
        <div>
          <h3 className="text-sm font-semibold">Contracts in Google Drive</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {hasExactLink
              ? "Opens this customer's contracts folder directly."
              : 'No folder link is recorded yet, so this opens the contracts library. Contracts are filed alphabetically, so pick the letter and then the company.'}{' '}
            Files open in Drive using your own Google account, so you will see whatever you normally
            have access to.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {hasExactLink && (
            <Button asChild>
              <a href={contractsFolderUrl as string} target="_blank" rel="noopener noreferrer">
                <FolderOpen className="mr-2 h-4 w-4" aria-hidden="true" />
                Open contracts folder
                {/*
                  The destination is another site, and a button reading only "Open" tells a
                  screen-reader user neither whose folder it is nor that they are leaving.
                */}
                <span className="sr-only">
                  {' '}
                  for {accountName || 'this account'} in Google Drive, opens in a new tab
                </span>
                <ExternalLink className="ml-2 h-3.5 w-3.5" aria-hidden="true" />
              </a>
            </Button>
          )}

          {!hasExactLink && (
            <Button asChild>
              <a href={contractsRootUrl()} target="_blank" rel="noopener noreferrer">
                <FolderSearch className="mr-2 h-4 w-4" aria-hidden="true" />
                Browse contracts in Drive
                <span className="sr-only">
                  {' '}
                  , opens the contracts library in Google Drive in a new tab. Contracts are filed
                  alphabetically, so open the letter for {accountName || 'this company'}, then the
                  company folder.
                </span>
                <ExternalLink className="ml-2 h-3.5 w-3.5" aria-hidden="true" />
              </a>
            </Button>
          )}

          {canEdit && !editing && (
            <Button
              variant="outline"
              onClick={() => {
                setDraft(contractsFolderUrl ?? '')
                setProblem(null)
                setEditing(true)
              }}
            >
              <Pencil className="mr-2 h-4 w-4" aria-hidden="true" />
              {hasExactLink ? 'Change folder link' : 'Set folder link'}
            </Button>
          )}
        </div>

        {canEdit && editing && (
          <div className="space-y-2 border-t pt-4">
            <label htmlFor="contracts-folder-url" className="text-sm font-medium block">
              Google Drive folder link
            </label>
            <p className="text-xs text-muted-foreground">
              Open this customer&rsquo;s folder in Drive and copy the address from your browser.
              Leave it empty to go back to searching by name.
            </p>
            <Input
              id="contracts-folder-url"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="https://drive.google.com/drive/folders/…"
              aria-describedby={problem ? 'contracts-folder-error' : undefined}
              aria-invalid={problem ? true : undefined}
            />
            {problem && (
              // role="alert" so it is announced when it appears, not only found on review.
              <p id="contracts-folder-error" role="alert" className="text-sm text-destructive">
                {problem}
              </p>
            )}
            <div className="flex gap-2">
              <Button onClick={() => save(draft)} disabled={updateAccount.isPending}>
                {updateAccount.isPending ? 'Saving…' : 'Save'}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setEditing(false)
                  setProblem(null)
                }}
                disabled={updateAccount.isPending}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
